import { lazy } from 'react';

import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import ProtectedRoute from 'components/ProtectedRoute';
import SuperadminGate from 'components/SuperadminGate';
import SuperadminOnly from 'components/SuperadminOnly';

const TenantList = Loadable(lazy(() => import('pages/tenants/TenantList')));
const TenantNew = Loadable(lazy(() => import('pages/tenants/TenantNew')));
const TenantEdit = Loadable(lazy(() => import('pages/tenants/TenantEdit')));
const TenantUsers = Loadable(lazy(() => import('pages/tenants/TenantUsers')));
const TenantWorkspace = Loadable(lazy(() => import('pages/tenants/TenantWorkspace')));
const TenantEditRedirect = Loadable(lazy(() => import('pages/tenants/TenantEditRedirect')));
const TenantAuditPage = Loadable(lazy(() => import('pages/tenants/TenantAuditPage')));
const TenantSetupAssistantPage = Loadable(lazy(() => import('pages/tenants/TenantSetupAssistantPage')));
const ClientList = Loadable(lazy(() => import('@crm/pages/clients/ClientList')));
const ClientDetail = Loadable(lazy(() => import('@crm/pages/clients/ClientDetail')));
const FiscalDocumentList = Loadable(lazy(() => import('@crm/pages/documents/FiscalDocumentList')));
const FiscalDocumentNew = Loadable(lazy(() => import('@crm/pages/documents/FiscalDocumentNew')));
const FiscalDocumentDetail = Loadable(lazy(() => import('@crm/pages/documents/FiscalDocumentDetail')));
const CollectionsQueue = Loadable(lazy(() => import('@crm/pages/collections/CollectionsQueue')));
const ProductList = Loadable(lazy(() => import('@crm/pages/products/ProductList')));
const ProductDetail = Loadable(lazy(() => import('@crm/pages/products/ProductDetail')));
const SupplierList = Loadable(lazy(() => import('@crm/pages/suppliers/SupplierList')));
const SupplierDetail = Loadable(lazy(() => import('@crm/pages/suppliers/SupplierDetail')));
const ReceivedDocumentList = Loadable(lazy(() => import('@crm/pages/receivedDocuments/ReceivedDocumentList')));
const IndustryList = Loadable(lazy(() => import('pages/admin/IndustryList')));
const IndustryDetail = Loadable(lazy(() => import('pages/admin/IndustryDetail')));
const RecordTypesSettingsPage = Loadable(lazy(() => import('@crm/pages/settings/RecordTypesSettingsPage')));
const AiSettingsPage = Loadable(lazy(() => import('pages/admin/AiSettingsPage')));
const CreditManagementPage = Loadable(lazy(() => import('pages/admin/CreditManagementPage')));
const TenantCreditManagementPage = Loadable(lazy(() => import('pages/admin/TenantCreditManagementPage')));
const CreditPricingPage = Loadable(lazy(() => import('pages/admin/CreditPricingPage')));
const AuditLogPage = Loadable(lazy(() => import('pages/audit-log/AuditLogPage')));
const ProfilePage = Loadable(lazy(() => import('pages/profile/ProfilePage')));
const HomeRedirect = Loadable(lazy(() => import('pages/sales/HomeRedirect')));
const SalesConversationList = Loadable(lazy(() => import('pages/sales/SalesConversationList')));
const SalesConversationDetail = Loadable(lazy(() => import('pages/sales/SalesConversationDetail')));
const ProspectList = Loadable(lazy(() => import('pages/prospects/ProspectList')));
const ProspectDetail = Loadable(lazy(() => import('pages/prospects/ProspectDetail')));

const MainRoutes = {
  path: '/',
  element: (
    <ProtectedRoute>
      <SuperadminGate>
        <DashboardLayout />
      </SuperadminGate>
    </ProtectedRoute>
  ),
  children: [
    { path: '/', element: <HomeRedirect /> },
    { path: 'sales-conversations', element: <SalesConversationList /> },
    { path: 'sales-conversations/:id', element: <SalesConversationDetail /> },
    {
      path: 'prospects',
      element: (
        <SuperadminOnly>
          <ProspectList />
        </SuperadminOnly>
      )
    },
    {
      path: 'prospects/new',
      element: (
        <SuperadminOnly>
          <ProspectDetail />
        </SuperadminOnly>
      )
    },
    {
      path: 'prospects/:id',
      element: (
        <SuperadminOnly>
          <ProspectDetail />
        </SuperadminOnly>
      )
    },
    {
      path: 'tenants',
      element: (
        <SuperadminOnly>
          <TenantList />
        </SuperadminOnly>
      )
    },
    {
      path: 'tenants/new',
      element: (
        <SuperadminOnly>
          <TenantNew />
        </SuperadminOnly>
      )
    },
    {
      path: 'tenants/:id/edit',
      element: (
        <SuperadminOnly>
          <TenantEditRedirect />
        </SuperadminOnly>
      )
    },
    {
      path: 'tenants/:tenantId',
      element: (
        <SuperadminOnly>
          <TenantWorkspace />
        </SuperadminOnly>
      ),
      children: [
        { index: true, element: <TenantEdit /> },
        { path: 'users', element: <TenantUsers /> },
        { path: 'clients', element: <ClientList /> },
        { path: 'clients/:id', element: <ClientDetail /> },
        { path: 'documents', element: <FiscalDocumentList /> },
        { path: 'documents/new', element: <FiscalDocumentNew /> },
        { path: 'documents/:id', element: <FiscalDocumentDetail /> },
        { path: 'collections', element: <CollectionsQueue /> },
        { path: 'products', element: <ProductList /> },
        { path: 'products/:id', element: <ProductDetail /> },
        { path: 'suppliers', element: <SupplierList /> },
        { path: 'suppliers/:id', element: <SupplierDetail /> },
        { path: 'received-documents', element: <ReceivedDocumentList /> },
        { path: 'record-types', element: <RecordTypesSettingsPage /> },
        { path: 'assistant', element: <TenantSetupAssistantPage /> },
        { path: 'audit', element: <TenantAuditPage /> }
      ]
    },
    {
      path: 'industries',
      element: (
        <SuperadminOnly>
          <IndustryList />
        </SuperadminOnly>
      )
    },
    {
      path: 'industries/new',
      element: (
        <SuperadminOnly>
          <IndustryDetail />
        </SuperadminOnly>
      )
    },
    {
      path: 'industries/:id',
      element: (
        <SuperadminOnly>
          <IndustryDetail />
        </SuperadminOnly>
      )
    },
    {
      path: 'ai',
      element: (
        <SuperadminOnly>
          <AiSettingsPage />
        </SuperadminOnly>
      )
    },
    {
      path: 'credits',
      element: (
        <SuperadminOnly>
          <CreditManagementPage />
        </SuperadminOnly>
      )
    },
    {
      path: 'credits/tenants/:tenantId',
      element: (
        <SuperadminOnly>
          <TenantCreditManagementPage />
        </SuperadminOnly>
      )
    },
    {
      path: 'credit-pricing',
      element: (
        <SuperadminOnly>
          <CreditPricingPage />
        </SuperadminOnly>
      )
    },
    {
      path: 'audit-log',
      element: (
        <SuperadminOnly>
          <AuditLogPage />
        </SuperadminOnly>
      )
    },
    { path: 'profile', element: <ProfilePage /> }
  ]
};

export default MainRoutes;
